import { Textarea, TextareaProps } from "../Textarea";
import { Icon } from "../Icon";
import { useChatClass, useTheme } from "../../theme";
import { BubbleContainer, ButtonContainer, Container } from "./styled";

export interface ChatInputProps extends TextareaProps {
  /** html id attribute */
  id: string;
  /** If true, does not allow the user to submit a response. */
  buffering: boolean;
  /** A callback to submit the user response.
   *
   * Will be called when the user presses the "Enter" key without the "Shift" key,
   * or clicks the send icon button */
  onSend?: () => any;
}

/**  An input control with a built-in submit button.
 *
 * Extends `<Textarea>` and adds a submit button to send the message.
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  id,
  onSend,
  buffering,
  value,
  ...props
}: ChatInputProps) => {
  const theme = useTheme();
  const chatClass = useChatClass();

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ): void => {
    event.stopPropagation();
    const { shiftKey } = event;

    if (event.key !== "Enter") return;
    if (event.key === "Enter" && !shiftKey) {
      event.preventDefault();
      onSend?.();
    }
  };

  return (
    <Container
      className={chatClass("input")}
      role="form"
      aria-label="Message input"
    >
      <Textarea
        id={id}
        onKeyDown={handleKeyPress}
        value={value}
        aria-label="Type your message"
        {...props}
      />
      <ButtonContainer htmlFor={id} $ready={Boolean(value) && !buffering}>
        <BubbleContainer
          onClick={onSend}
          $size="small"
          aria-label="Send message"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSend?.();
            }
          }}
        >
          <Icon
            name="small-arrow-up"
            size={16}
            color={theme.colors.white}
            aria-hidden="true"
          />
        </BubbleContainer>
      </ButtonContainer>
    </Container>
  );
};
